/** Shown whenever the API supplies no customer-safe message of its own. */
export const PAYMENT_METHOD_GENERIC_ERROR =
	'Something went wrong. Please try again, or contact support if the problem persists.';

/**
 * The subset of a payment API error considered safe to show a customer. Not taken from
 * `ApiErrorI`, which does not declare these — club-sauce spreads the server payload into
 * the error and camel-cases it, so the fields arrive without being part of the type.
 */
interface PaymentApiErrorLikeI {
	details?: unknown;
	publicMessage?: unknown;
}

const asNonEmptyString = (value: unknown): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}

	const trimmed = value.trim();

	return trimmed === '' ? undefined : trimmed;
};

/**
 * Extracts a customer-safe message from a payment API error, falling back to a generic
 * string when the API offers nothing suitable.
 *
 * Deliberately does **not** use the shared `getErrorStringHelper`, which falls through to
 * `fullMessage`. That field carries internal class names and processor vocabulary, and
 * some of those messages are security boundaries, so it must never reach a customer.
 * `errorClass` is excluded for the same reason. Only `publicMessage` and `details` are
 * treated as safe.
 */
const getPaymentMethodErrorMessage = (error: unknown): string => {
	if (typeof error !== 'object' || error === null) {
		return PAYMENT_METHOD_GENERIC_ERROR;
	}

	const { publicMessage, details } = error as PaymentApiErrorLikeI;

	return (
		asNonEmptyString(publicMessage) ??
		asNonEmptyString(details) ??
		PAYMENT_METHOD_GENERIC_ERROR
	);
};

export default getPaymentMethodErrorMessage;
